import { NoteAdd as NoteAddIcon, Warning as WarningIcon } from "@mui/icons-material";
import { CircularProgress, Grid, Typography } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { AsolviSwitch } from "components/AsolviSwitch";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { EditPhoto } from "components/editPhoto/EditPhoto";
import { FilePreview } from "components/filePreview/FilePreview";
import { LinkPreview } from "components/filePreview/LinkPreview";
import FullScreenDialog from "components/FullScreenDialog";
import { PromptDialog } from "components/PromptDialog";
import { StyledContainer } from "components/StyledContainer";
import { TabProps } from "models/TabProps";
import { VisitFile } from "models/visitFile";
import { VisitLink } from "models/visitLink";
import { FileType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  getFiles,
  removeFile,
  removeLink,
  selectSelectedJob,
  selectSelectedJobVisit,
  setEditFileOpen,
  setEditLinkOpen,
  updateFile,
  uploadFile,
} from "store/slices/jobs.store";
import { isFlagEnabled } from "store/slices/user.store";
import { FileTabActions } from "./FileTabActions";

export const FileTab: FC<TabProps> = (props) => {
  const { setFab } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { fileUploading } = useAppSelector((s) => s.jobs);
  const { openEditFile, fileToEdit } = useAppSelector((s) => s.jobs.fileTab);
  const { files, links } = useAppSelector(selectSelectedJobVisit);
  const {
    externalId,
    files: downloadedFiles,
    links: downloadedLinks,
    id: jobId,
  } = useAppSelector(selectSelectedJob);
  const documentWeblinksEnabled = useAppSelector((s) => isFlagEnabled(s, "DocumentWebLinks"));
  const [openRemoveFileDialog, setOpenRemoveFileDialog] = useState(false);
  const [openRemoveLinkDialog, setOpenRemoveLinkDialog] = useState(false);
  const [removeId, setRemoveId] = useState<string | undefined>(undefined);
  const [openCloseConfirmation, setOpenCloseConfirmation] = useState(false);
  const [isFullSize, setIsFullSize] = useState(false);

  const onChangeUseInServiceReport = useCallback(
    (file: FileType, downloaded: boolean) => {
      if (downloaded) {
        dispatch(uploadFile({ jobId, file }))
          .then(unwrapResult)
          .then(() => {
            dispatch(getFiles());
          });
      } else {
        dispatch(updateFile({ file }));
      }
    },
    [dispatch, jobId],
  );

  const toggleFullSize = useCallback((value: boolean) => {
    localStorage.setItem("uncompressFiles", JSON.stringify(value));
    setIsFullSize(value);
  }, []);

  const allFiles = useMemo(() => {
    return [
      ...downloadedFiles.map(
        (j) =>
          ({
            file: j,
            downloaded: true,
          }) as VisitFile,
      ),
      ...files.map(
        (j) =>
          ({
            file: j,
            downloaded: false,
          }) as VisitFile,
      ),
    ];
  }, [downloadedFiles, files]);

  const allLinks = useMemo(() => {
    return [
      ...downloadedLinks.map(
        (j) =>
          ({
            link: j,
            downloaded: true,
          }) as VisitLink,
      ),
      ...links.map(
        (j) =>
          ({
            link: j,
            downloaded: false,
          }) as VisitLink,
      ),
    ];
  }, [downloadedLinks, links]);

  useEffect(() => {
    setIsFullSize(JSON.parse(localStorage.getItem("uncompressFiles") ?? "false") || false);
  }, []);

  return (
    <StyledContainer data-testid="FileTab">
      <BackdropPrimaryMain open={fileUploading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ paddingTop: "20px", paddingBottom: "20px" }}
      >
        <Grid item>
          <Typography
            variant="body1"
            onClick={() => {
              toggleFullSize(!isFullSize);
            }}
          >
            <FormattedMessage id="file.uncompressImages" />
          </Typography>
        </Grid>
        <Grid item>
          <AsolviSwitch
            key="FullSizeSwitch"
            checked={isFullSize}
            onChange={() => {
              toggleFullSize(!isFullSize);
            }}
            data-testid="FileTab-FullSize-Toggle"
          />
        </Grid>
      </Grid>

      {isFullSize && (
        <Grid container sx={{ mb: 2 }}>
          <Grid item>
            <Typography>
              <WarningIcon color="warning" sx={{ mr: 1.5, mb: -0.5 }} fontSize="medium" />
              <FormattedMessage id="file.fullSizeWarningText" />
            </Typography>
          </Grid>
        </Grid>
      )}

      {allFiles.length === 0 && allLinks.length === 0 && (
        <Grid container direction="column" alignItems="center">
          <Grid item>
            <NoteAddIcon color="secondary" fontSize="large" />
          </Grid>
          <Grid item>
            <Typography color="secondary">
              <FormattedMessage id="file.noFilesSelected" />
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={1}>
        {documentWeblinksEnabled &&
          allLinks.map(({ link, downloaded }, index) => (
            <LinkPreview
              key={index}
              link={link}
              onDelete={() => {
                setRemoveId(link.id);
                setOpenRemoveLinkDialog(true);
              }}
              onEdit={() => {
                dispatch(setEditLinkOpen({ open: true, link: link }));
              }}
              readonly={downloaded}
            />
          ))}
        {allFiles.map(({ file, downloaded }, index) => (
          <FilePreview
            key={index}
            file={file}
            onDelete={() => {
              setRemoveId(file.id!);
              setOpenRemoveFileDialog(true);
            }}
            onEdit={() => {
              dispatch(setEditFileOpen({ open: true, file: file }));
            }}
            data-testid={`FilePreview-${index}`}
            disableEdit={downloaded || file.isFullSize || false}
            downloadOnCardClick={downloaded}
            readonly={downloaded}
            onChangeUseInServiceReport={(value: boolean) => {
              onChangeUseInServiceReport({ ...file, useInServiceReport: value }, downloaded);
            }}
          />
        ))}
      </Grid>
      <FileTabActions setFab={setFab} isFullSize={isFullSize} />

      <FullScreenDialog
        open={openEditFile}
        setOpen={(open: boolean) => setOpenCloseConfirmation(!open)}
        title={`${externalId} / ${intl.formatMessage({
          id: "file.editImage",
        })}`}
        centerTitle
        goingBack
        child={
          <EditPhoto
            onClose={() => dispatch(setEditFileOpen({ open: false, file: undefined }))}
            currentPhoto={fileToEdit}
            onSave={(file) => {
              dispatch(updateFile({ file }));
            }}
            openCloseConfirmation={openCloseConfirmation}
            setOpenCloseConfirmation={setOpenCloseConfirmation}
          />
        }
      />
      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={openRemoveFileDialog}
        setOpen={setOpenRemoveFileDialog}
        okText={intl.formatMessage({ id: "general.confirm" })}
        onOk={() => dispatch(removeFile({ id: removeId! }))}
        onCancel={() => setOpenRemoveFileDialog(false)}
        title={<FormattedMessage id="file.remove" />}
        promptContent={<FormattedMessage id="visit.file.removePrompt" />}
      />

      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={openRemoveLinkDialog}
        setOpen={setOpenRemoveLinkDialog}
        okText={intl.formatMessage({ id: "general.confirm" })}
        onOk={() => dispatch(removeLink({ id: removeId! }))}
        onCancel={() => setOpenRemoveLinkDialog(false)}
        title={<FormattedMessage id="file.removeLink" />}
        promptContent={<FormattedMessage id="file.removeLinkPrompt" />}
      />
    </StyledContainer>
  );
};
